/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModal } from '../../contexts/ModalContext'
import AddFolderModal from '../addFolderModal/AddFolderModal'
import AddFolderStructureModal from './AddFolderStructureModal'
import { Modal } from '../Modal'

interface AddFolderChoiceModalProps {
  parentFsi: {
    id: number
    name?: string
    systemFolderType?: string
  }
}

const AddFolderChoiceModal: React.FC<AddFolderChoiceModalProps> = ({ parentFsi }) => {
  const { t } = useTranslation()
  const { showModal, hideModal } = useModal()
  const [docChoice, setDocChoice] = useState<'single' | 'structure'>('single')

  const handleSubmit = () => {
    // Close current modal first
    hideModal()
    // Open the appropriate next modal
    switch (docChoice) {
      case 'single':
        showModal(<AddFolderModal parentFolderId={parentFsi.id} />)
        break
      case 'structure':
        showModal(<AddFolderStructureModal parentId={parentFsi.id} />)
        break
    }
  }

  const handleClose = () => {
    hideModal()
  }

  return (
    <Modal
      show={true}
      handleClose={handleClose}
      size="md"
      id="modal-add-file"
      className="doc-choice-modal-wrapper theme-sandman"
      dataTestId="modal-add-file"
      title={t('BROWSER_ADD_FOLDERS_H_TITLE', 'Add Folders')}
    >
      <Modal.Body>
        <form
          name="AddFolderForm"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <div className="form-group md-form-group checkbox-form-group w100p mb10">
            <input
              type="radio"
              id="input-single-folder"
              name="classification"
              value="single"
              checked={docChoice === 'single'}
              onChange={() => setDocChoice('single')}
            />
            <label htmlFor="input-single-folder">
              <div className="chkbc"></div>
              <div className="doc-choice-type">{t('BROWSER_ADD_FOLDER_SINGLE_FOLDER_LABEL', 'Single Folder')}</div>
            </label>
          </div>
          <div className="form-group md-form-group checkbox-form-group w100p">
            <input
              type="radio"
              id="input-folder-structure"
              name="classification"
              value="structure"
              checked={docChoice === 'structure'}
              onChange={() => setDocChoice('structure')}
            />
            <label htmlFor="input-folder-structure">
              <div className="chkbc"></div>
              <div className="doc-choice-type">
                {t('BROWSER_ADD_FOLDER_FOLDER_STRUCTURE_LABEL', 'Folder Structure')}
              </div>
              <small className="help-text">
                {t('BROWSER_ADD_FOLDER_FOLDER_STRUCTURE_HELP_INFO', 'Add a predefined folder structure template')}
              </small>
            </label>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-close" onClick={handleClose}>
          {t('CANCEL', 'Cancel')}
        </button>
        <button className="btn btn-primary" onClick={handleSubmit}>
          {t('NEXT', 'Next')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddFolderChoiceModal
